import { type NoteOutcomeEntity } from '@apps/web/lib/agent_note_outcome_copy';
import {
    useCaregiverMemory,
    useClientMemory,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const REASON = 'saved-note-record-preview';

/**
 * The person's memory, read-only.
 *
 * Deliberately not the profile's MemoryTab: that is an editing surface, with a
 * recompile control and an instruction composer, and a chat row is somewhere
 * you check what Phoebe knows rather than somewhere you edit it. The header's
 * badge opens the full record for anyone who wants to change it.
 *
 * Only mounted once a row is expanded, so a turn carrying several notes
 * fetches nothing until asked.
 */
/**
 * The compiled memory, or null while it is still being written.
 *
 * Exported so the card can hold the whole row back until there is a record to
 * show. SWR dedupes on the key, so calling this from both places is one fetch.
 */
export function useMemoryRecordMarkdown(
    organizationId: string | null,
    entity: NoteOutcomeEntity | undefined,
): string | null {
    const isCaregiver = entity?.type === 'caregiver';
    const caregiver = useCaregiverMemory(
        organizationId,
        isCaregiver ? (entity?.id ?? null) : null,
        { reason: REASON },
    );
    const client = useClientMemory(
        organizationId,
        isCaregiver ? null : (entity?.id ?? null),
        { reason: REASON },
    );
    const source = isCaregiver ? caregiver : client;
    return source.data?.compiled_markdown?.trim() || null;
}
